import { InteractionType } from '@azure/msal-browser';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { CircularProgress, Stack } from '@mui/material';
import { getSdxAdminServer } from '@psw/shared';
import { SdxAdmin, type SdxAdminProps } from '@sodexo/admin-module-react';
import { SdxColors } from '@sodexo/ui-components';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { lazy, Suspense, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useInitApp } from '../Effects/useInitApp';
import { FetchService, queryClient } from '../services/fetch.service';
import PersistentDrawerLeft from './TopBar/PersistentDrawerLeft';

export function AuthenticatedGuard() {
  const [accessToken, setAccessToken] = useState<string>();
  const { instance, accounts } = useMsal();

  useEffect(() => {
    (async () => {
      if (accounts.length > 0) {
        instance.setActiveAccount(accounts[0]);
        const token = await FetchService.generateToken();
        if (!token) throw new Error('Token not found');
        setAccessToken(token.accessToken);
      }
    })();
  }, [accounts]);

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      errorComponent={props => <p>{JSON.stringify(props)}</p>}
      loadingComponent={MsalLoadingComponent}
    >
      {accessToken && (
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools initialIsOpen={false} buttonPosition='bottom-left' />
          <SdxAdmin
            unsafeAPIKey={process.env.SDX_ADMIN_API_KEY}
            accessToken={accessToken}
            projectId={process.env.SDX_ADMIN_PROJECT_ID!}
            server={getAdminModuleServer()}
            clientId={process.env.AAD_CLIENT_ID}
          >
            <AuthenticatedInitGuard />
          </SdxAdmin>
        </QueryClientProvider>
      )}
    </MsalAuthenticationTemplate>
  );
}

function MsalLoadingComponent() {
  return (
    <Stack height='100dvh' justifyContent='center' alignItems='center' flex={1}>
      <CircularProgress />
    </Stack>
  );
}

const GlobalUnauthorizedAccess = lazy(() => import('./GlobalUnauthorizedAccess'));

function AuthenticatedInitGuard() {
  const { done, forbidden } = useInitApp();

  if (forbidden) {
    return (
      <Suspense fallback={<CircularProgress />}>
        <GlobalUnauthorizedAccess />
      </Suspense>
    );
  }

  if (done) {
    return (
      <PersistentDrawerLeft>
        <Outlet />
      </PersistentDrawerLeft>
    );
  }

  return (
    <Stack spacing={2} height='100dvh' alignItems='center' justifyContent='center'>
      <CircularProgress sx={{ color: SdxColors.blueDarker }} />
    </Stack>
  );
}

function getAdminModuleServer(): SdxAdminProps['server'] {
  if (process.env.IS_LOCAL) return 'sandbox_sdx';

  return getSdxAdminServer(process.env.NODE_ENV);
}
